import CallModal from 'app/components/call/CallModal';
import BrowserSpecific from 'app/components/common/BrowserSpecific';
import { FirefoxPermission } from 'app/components/DeviceSetup/browserScreens';
import WithSkip from 'app/components/DeviceSetup/WithSkip';
import MarginGroup from 'app/components/layout/MarginGroup';
import { tFunctionType, useI18n } from 'app/components/localization';
import LoadingButtonA from 'app/components/presentation/buttons/LoadingButtonA';
import {
  useDeviceManager,
  useDeviceManagerState,
  useIsDeviceManagerBusy,
} from 'app/containers/rtc/DeviceManagerContext';
import { DeviceType } from 'app/services/rtc/DeviceManager';
import { Browser, OperatingSystem, Platform } from 'app/utils/userAgent';
import React, { Fragment, FunctionComponent } from 'react';

const messagesPerType = {
  [DeviceType.Audio]: (t: tFunctionType) => ({
    title: t('rtc.device-setup.initial.audio.title'),
    message: {
      skippable: t('rtc.device-setup.initial.audio.message.skippable'),
      required: t('rtc.device-setup.initial.audio.message.required'),
    },
  }),
  [DeviceType.Video]: (t: tFunctionType) => ({
    title: t('rtc.device-setup.initial.video.title'),
    message: {
      skippable: t('rtc.device-setup.initial.video.message.skippable'),
      required: t('rtc.device-setup.initial.video.message.required'),
    },
  }),
};

interface IProps {
  type: DeviceType;
}

const InitialSetup: FunctionComponent<IProps> = ({ type }) => {
  const deviceManager = useDeviceManager();
  const deviceManagerState = useDeviceManagerState();
  const isDeviceManagerBusy = useIsDeviceManagerBusy();
  const { t } = useI18n();

  const { message, title } = messagesPerType[type](t);

  return (
    <CallModal
      buttons={
        <WithSkip type={type}>
          <LoadingButtonA onClick={deviceManager.step} isLoading={isDeviceManagerBusy}>
            {t('rtc.device-setup.initial.enable')}
          </LoadingButtonA>
        </WithSkip>
      }
      onClose={deviceManager.dismiss}
      title={title}
    >
      <MarginGroup margin="1em">
        <p>{deviceManagerState[type].behaviorOptions.required ? message.required : message.skippable}</p>
        <BrowserSpecific>{({ platform, browser, os }) =>
          <Fragment>
            {
              // Tell FF users to check the "Remember this setting" box
              platform === Platform.PC &&
              browser === Browser.Firefox && (
                <Fragment>
                  <p>{t('rtc.device-setup.initial.firefox.check-box')}</p>
                  <FirefoxPermission />
                </Fragment>
              )
            }

            {
              // Android, iOS and macOS have also os-level
              // prompts for each app. On iOS and macOS this
              // doesn't apply to Safari, only to third-party
              // apps.
              browser !== Browser.Safari && (
                os === OperatingSystem.Android ||
                os === OperatingSystem.macOS ||
                os === OperatingSystem.iOS
              ) && (
                <p>{t('rtc.device-setup.initial.os-permission')}</p>
              )
            }

            {
              // Safari on iOS is not subjected to os-level
              // permissions (being a first-party app) but in
              // its own preferences panel it allows to disable
              // camera / microphone access
              os === OperatingSystem.iOS &&
              browser === Browser.Safari && (
                <p>{t('rtc.device-setup.initial.safari.ios-setting')}</p>
              )
            }
          </Fragment>
        }</BrowserSpecific>
      </MarginGroup>
    </CallModal>
  );
};

export default InitialSetup;
